import * as React from 'react';
import type { ChangeHook, EnterHook } from 'react-router';
import { Route } from 'react-router';

import { omit } from 'lodash';

import { Route as CourseraRoute } from '@coursera/react-router';

import ClipsApp from 'bundles/clips/components/ClipsApp';
import loadOnRoute from 'bundles/common/components/loadOnRoute';
import LearnerOnboardingPage from 'bundles/enterprise-learner-onboarding/components/LearnerOnboardingContainer';
import RouteNames from 'bundles/enterprise-legacy-learner-home/constants/RouteNames';
import ProgramHomeApp from 'bundles/program-home/components/ProgramHomeApp';
import EnterpriseDomainsPage from 'bundles/program-home/components/enterprise-home/EnterpriseDomainsPage';
import EnterpriseHomePage from 'bundles/program-home/components/enterprise-home/EnterpriseHomePage';
import LoginRouteRedirect from 'bundles/program-home/components/enterprise-home/LoginRouteRedirect';
import ProgramsAllRouteHandler from 'bundles/program-home/components/enterprise-home/ProgramsAllRouteHandler';

import { ProgramHomePageTypes } from '../enterprise-legacy-learner-home/constants/ProgramHomeConstants';

const EnterpriseSearchPage = loadOnRoute(() => import('bundles/program-home/components/search/EnterpriseSearchPage'));

const OldModalRouteRedirect = loadOnRoute(
  () => import('bundles/enterprise-collections/components/OldModalRouteRedirect')
);

const OrgPrivacyNoticePage = loadOnRoute(
  () => import('bundles/enterprise-legacy-learner-home/components/org-privacy-notice/OrgPrivacyNoticePage')
);

const ClipsRootContainer = () => import('bundles/clips/components/ClipsRootContainer');

const NotFound = loadOnRoute(() => import('bundles/phoenix/components/NotFound'));

const scrollToTopOnNavigate: ChangeHook = (prevState, nextState) => {
  if (nextState.location.action !== 'PUSH') {
    // history navigation
    return;
  }

  if (nextState.location.pathname !== prevState.location.pathname) {
    // always on path navigation
    window.scrollTo(0, 0);
  }
};

const onEnterProgramHome: EnterHook = (nextState, replace) => {
  const { deepLinkItemId, attemptSSOLogin, itemId, productType } = nextState.location.query;

  // If the user is about to be logged in through SSO with an itemId, add the deeplink param for consumption by the
  // minimodal for autoenrollment and deeplinking
  if (!deepLinkItemId && attemptSSOLogin && itemId && productType === 'course') {
    replace({
      pathname: nextState.location.pathname,
      query: { ...omit(nextState.location.query, 'itemId'), deepLinkItemId: itemId },
    });
  }
};

const routes = (
  <CourseraRoute
    onChange={scrollToTopOnNavigate}
    handle={{
      tracking: {
        data: (params) => ({
          page: { segment: 'enterprise' },
          enterprise: {
            organizationSlug: params?.orgSlug,
            ...(params?.programSlug ? { programSlug: params.programSlug } : {}),
          },
        }),
      },
    }}
  >
    <Route name={RouteNames.ENTERPRISE_LEARNER_ROOT} path="programs/all" component={ProgramsAllRouteHandler}>
      <CourseraRoute
        name={RouteNames.ENTERPRISE_LEARNER_SEARCH}
        path=":orgSlug/browse"
        getComponent={EnterpriseSearchPage}
        handle={{
          tracking: {
            data: (_, location) => {
              const currentPage = location?.query?.page;

              return {
                page: {
                  type: ProgramHomePageTypes.ENTERPRISE_SEARCH,
                  pagination: {
                    pageNumber: currentPage ? Number(currentPage) : 1,
                  },
                },
              };
            },
          },
        }}
      />
      <CourseraRoute
        name={RouteNames.ENTERPRISE_LEARNER_DOMAINS}
        path=":orgSlug/:domainSlug(/:subdomainSlug)"
        component={EnterpriseDomainsPage}
        handle={{
          tracking: {
            data: () => ({
              page: { type: ProgramHomePageTypes.SKILLSETS },
            }),
          },
        }}
      />
      <CourseraRoute
        name={RouteNames.ENTERPRISE_LEARNER_HOME}
        path=":orgSlug"
        component={EnterpriseHomePage}
        handle={{
          tracking: {
            data: () => ({
              page: { type: ProgramHomePageTypes.HOME },
            }),
          },
        }}
      />
    </Route>

    <CourseraRoute
      name={RouteNames.ENTERPRISE_PRIVACY_NOTICE}
      path="programs/privacy-notice/:orgSlug"
      getComponent={OrgPrivacyNoticePage}
      handle={{
        tracking: {
          data: () => ({
            page: { type: ProgramHomePageTypes.HOME },
          }),
        },
      }}
    />

    <CourseraRoute
      name={RouteNames.PROGRAM_HOME}
      path="programs(/:programSlug)"
      component={ProgramHomeApp}
      onEnter={onEnterProgramHome}
      handle={{
        tracking: {
          data: (_, location) => {
            const currentTab = location?.query?.currentTab as keyof typeof ProgramHomePageTypes;

            return {
              page: { type: ProgramHomePageTypes[currentTab] ?? ProgramHomePageTypes.CATALOG },
            };
          },
        },
      }}
    >
      <Route name={RouteNames.LEARNER_ONBOARDING} path="onboarding" component={LearnerOnboardingPage} />
      <Route name="profile" path="login" component={LoginRouteRedirect} />
      <Route
        name={RouteNames.MINI_PAGE}
        path="modal(/:type(/:id(/:actionType)))"
        getComponent={OldModalRouteRedirect}
      />
    </CourseraRoute>
    <Route path="/" component={ClipsApp}>
      <Route path="clips/:courseSlug/:itemId" getComponent={loadOnRoute(ClipsRootContainer)} />;
      <Route path="videos/:courseSlug/:itemId" getComponent={loadOnRoute(ClipsRootContainer)} />;
    </Route>
    <Route path="*" getComponent={NotFound} />
  </CourseraRoute>
);

export default routes;
